import React, { useCallback, useEffect, useRef, useState } from "react";
import { MessageSquare, RefreshCw, Sparkles } from "lucide-react";
import { chatWithSupportAgent } from "./ticketApi";
import {
  RecommendationCard,
  LiveActivity,
  UserChatBubble,
  AgentChatBubble,
  ChatComposer,
  ShareSessionButton,
  STEP_TOOL_LABELS,
  describeStepInput,
} from "./TicketAnalysisPanel";
import {
  DoctorCard,
  PatientCardById,
  TicketSummaryCard,
} from "./ticketCards";
import { chatSurfaceClasses, chatSurfaceStyle } from "./chatBackground";
import {
  supportContextCardTitle,
  supportInsetPanelAuto,
  supportSubpanelHeader,
} from "./supportStyles";

const DEFAULT_SUGGESTIONS = [
  "Summarize ticket 1234 and tell me what's going on",
  "What does the pipeline show for patient 12345678?",
  "Look up Dr. Smith and list their details",
  "What should I do about ticket 1234?",
];

const DEFAULT_INTRO =
  "Ask anything — mention a ticket number, patient ID, or doctor name in your message and the agent will pull up the details itself.";

/**
 * Free-form chat with the support agent.
 *
 * With no props it starts blank (CS homepage): the agent retrieves tickets,
 * patients, doctors, and pipeline runs itself when the message references
 * them (e.g. "summarize ticket 1234").
 *
 * When embedded on a page that already has data loaded (e.g. the patient
 * lookup page), pass `seedContext` ({ patient, doctor, validator_results,
 * smartChart }) — it is sent along with the first message so the agent
 * starts out knowing the on-screen patient, doctor, validator, and Smart
 * Chart flag state.
 */
const SupportAgentChat = ({
  seedContext = null,
  suggestions = DEFAULT_SUGGESTIONS,
  intro = DEFAULT_INTRO,
  placeholder = "e.g. Summarize ticket 1234, or look up patient 12345678…",
  shareable = false,
  restored = null,
} = {}) => {
  const [conversation, setConversation] = useState(
    restored?.conversation || []
  );
  const [sessionId, setSessionId] = useState(restored?.sessionId || null);
  const [chatInput, setChatInput] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [activity, setActivity] = useState([]);
  const [agentError, setAgentError] = useState(null);
  // Entities the agent retrieved during the conversation, shown as info
  // cards above the thread: { patients: [ids], doctors: [rows], tickets: [] }
  const [resolved, setResolved] = useState(
    restored?.resolved || { patients: [], doctors: [], tickets: [] }
  );
  const bottomRef = useRef(null);

  useEffect(() => {
    if (conversation.length > 0 || activity.length > 0) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [conversation, activity]);

  const handleAgentEvent = useCallback((event) => {
    if (event.type === "patient_resolved" && event.patient?.patient_id) {
      const id = String(event.patient.patient_id);
      setResolved((prev) =>
        prev.patients.includes(id)
          ? prev
          : { ...prev, patients: [...prev.patients, id] }
      );
    }
    if (event.type === "doctor_resolved" && event.doctor?.user_id) {
      setResolved((prev) =>
        prev.doctors.some(
          (d) => String(d.user_id) === String(event.doctor.user_id)
        )
          ? prev
          : { ...prev, doctors: [...prev.doctors, event.doctor] }
      );
    }
    if (event.type === "ticket_resolved" && event.ticket) {
      const key = String(event.ticket.number ?? event.ticket.id ?? "");
      setResolved((prev) =>
        prev.tickets.some(
          (t) => String(t.ticket.number ?? t.ticket.id ?? "") === key
        )
          ? prev
          : {
              ...prev,
              tickets: [
                ...prev.tickets,
                { ticket: event.ticket, requester: event.requester },
              ],
            }
      );
    }
    setActivity((prev) => {
      if (event.type === "status") {
        return [...prev, { label: event.message }];
      }
      if (event.type === "tool_start") {
        return [
          ...prev,
          {
            tool: event.tool,
            label: STEP_TOOL_LABELS[event.tool] || event.tool,
            detail: describeStepInput(event.input),
          },
        ];
      }
      if (event.type === "step") {
        let index = -1;
        for (let i = prev.length - 1; i >= 0; i--) {
          if (prev[i].tool === event.tool && !prev[i].summary) {
            index = i;
            break;
          }
        }
        if (index === -1) return prev;
        const next = [...prev];
        next[index] = { ...next[index], summary: event.summary };
        return next;
      }
      if (event.type === "patient_resolved") {
        const patient = event.patient || {};
        return [
          ...prev,
          {
            label: `Patient identified: ${patient.description || "—"} (ID ${patient.patient_id})`,
          },
        ];
      }
      return prev;
    });
  }, []);

  const sendMessage = async (message) => {
    if (!message || isProcessing) return;

    setChatInput("");
    setAgentError(null);
    setActivity([]);
    setConversation((prev) => [...prev, { kind: "user", text: message }]);
    setIsProcessing(true);

    const data = await chatWithSupportAgent({
      sessionId,
      message,
      seedContext,
      onEvent: handleAgentEvent,
    });

    setIsProcessing(false);

    if (data.aborted) return;

    if (data.error) {
      setAgentError(data.error);
      return;
    }

    if (data.session_id) setSessionId(data.session_id);

    if (data.resolved_patient?.patient_id) {
      const id = String(data.resolved_patient.patient_id);
      setResolved((prev) =>
        prev.patients.includes(id)
          ? prev
          : { ...prev, patients: [...prev.patients, id] }
      );
    }

    if (data.type === "reply") {
      setConversation((prev) => [...prev, { kind: "reply", text: data.text }]);
    } else {
      setConversation((prev) => [
        ...prev,
        { kind: "recommendation", result: data },
      ]);
    }
  };

  const handleNewChat = () => {
    if (
      conversation.length > 0 &&
      !window.confirm(
        "Start a new chat? The current conversation will be lost."
      )
    ) {
      return;
    }
    setConversation([]);
    setSessionId(null);
    setChatInput("");
    setActivity([]);
    setAgentError(null);
    setResolved({ patients: [], doctors: [], tickets: [] });
  };

  const hasResolvedEntities =
    resolved.patients.length > 0 ||
    resolved.doctors.length > 0 ||
    resolved.tickets.length > 0;

  return (
    <div className={`${supportInsetPanelAuto} overflow-hidden`}>
      <div
        className={`${supportSubpanelHeader} flex items-center justify-between gap-2`}
      >
        <h4 className={supportContextCardTitle}>
          <Sparkles
            className="inline h-3.5 w-3.5 mr-1 text-ktx-purple -translate-y-px"
            aria-hidden
          />
          Chat with the support agent
        </h4>
        {conversation.length > 0 && !isProcessing && (
          <div className="flex items-center gap-4">
            {shareable && sessionId && (
              <ShareSessionButton
                sessionId={sessionId}
                buildUi={() => ({
                  patient_id: seedContext?.patient?.patient_id
                    ? String(seedContext.patient.patient_id)
                    : null,
                  conversation,
                  resolved,
                })}
              />
            )}
            <button
              type="button"
              onClick={handleNewChat}
              className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-ktx-purple"
            >
              <RefreshCw className="h-3 w-3" aria-hidden />
              New chat
            </button>
          </div>
        )}
      </div>

      <div className="px-3 py-3 space-y-3">
        {conversation.length === 0 && !isProcessing && (
          <div className="text-sm text-gray-600 space-y-2">
            <p className="flex items-start gap-2">
              <MessageSquare
                className="h-4 w-4 mt-0.5 text-ktx-purple shrink-0"
                aria-hidden
              />
              <span>{intro}</span>
            </p>
            <div className="flex flex-wrap gap-1.5">
              {suggestions.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  onClick={() => setChatInput(suggestion)}
                  className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs text-gray-600 hover:border-purple-300 hover:bg-purple-50 hover:text-ktx-purple transition-colors"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {hasResolvedEntities && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-start">
            {resolved.tickets.map((entry) => (
              <TicketSummaryCard
                key={String(entry.ticket.number ?? entry.ticket.id)}
                ticket={entry.ticket}
                requester={entry.requester}
                linkToTicketPage
              />
            ))}
            {resolved.doctors.map((doc) => (
              <DoctorCard key={doc.user_id} doctor={doc} />
            ))}
            {resolved.patients.map((pid) => (
              <PatientCardById key={pid} patientId={pid} />
            ))}
          </div>
        )}

        {(conversation.length > 0 || isProcessing || agentError) && (
          <div
            className={`${chatSurfaceClasses} p-3 space-y-3`}
            style={chatSurfaceStyle}
          >
            {conversation.map((entry, index) => {
              if (entry.kind === "user") {
                return <UserChatBubble key={index} text={entry.text} />;
              }
              if (entry.kind === "reply") {
                return <AgentChatBubble key={index} text={entry.text} />;
              }
              return (
                <div key={index} className={`${supportInsetPanelAuto} overflow-hidden`}>
                  <div className={supportSubpanelHeader}>
                    <h4 className={supportContextCardTitle}>
                      <Sparkles
                        className="inline h-3.5 w-3.5 mr-1 text-ktx-purple -translate-y-px"
                        aria-hidden
                      />
                      Agent recommendation
                    </h4>
                  </div>
                  <div className="px-3 py-3">
                    <RecommendationCard
                      result={entry.result}
                      sessionId={sessionId}
                      buildShareUi={() => ({
                        patient_id: seedContext?.patient?.patient_id
                          ? String(seedContext.patient.patient_id)
                          : null,
                        conversation,
                        resolved,
                      })}
                    />
                  </div>
                </div>
              );
            })}

            {isProcessing && <LiveActivity items={activity} />}

            {agentError && !isProcessing && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                {agentError}
              </p>
            )}
          </div>
        )}

        <div ref={bottomRef} />

        <ChatComposer
          value={chatInput}
          onChange={setChatInput}
          onSubmit={() => sendMessage(chatInput.trim())}
          disabled={isProcessing}
          placeholder={isProcessing ? "The agent is working…" : placeholder}
        />
      </div>
    </div>
  );
};

export default SupportAgentChat;
